import Radio from './Radio'
import Input from './Input'
import Income from './Income'
import { Field } from 'formik'
import Checkbox from './Checkbox'
import React, { FC } from 'react'
import MultiButton from './MultiButton'
import BinaryButton from './BinaryButton'
import Uploader from './compare/Uploader'
import InfoAccordion from './InfoAccordion'
import CheckboxMulti from './CheckboxMulti'
import RateSelection from '@mortgage-pos/ui/pages/RateSelection'
import SelectDropdown from './SelectDropdown'
import NtlRateSelection from '@mortgage-pos/ui/pages/NtlRateSelection'
import MultiSelectButton from './MultiSelectButton'
import LoaderWithAPICalls from './LoaderWithAPICalls'
import BankrateUploader from '@mortgage-pos/ui/components/bankrateCompare/Uploader'
import { useStoreState, useStoreActions } from '@mortgage-pos/ui/store'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { RepeatableAutoComplete } from '@mortgage-pos/ui/components/addressAutoComplete'
import CompareLERateSelection from './compare/CompareLERateSelection'
import { FEATURE_FLAG_TYPES, stateTextValuePairs } from '@mortgage-pos/data'
import { mapStatesDropdownData } from '@mortgage-pos/ui/utils/mapStatesDropdownData'
import BankrateCompareLERateSelection from '@mortgage-pos/ui/components/bankrateCompare/CompareLERateSelection'
import { PlaidAssets } from './plaid/PlaidAssets'

import {
  QuestionType,
  QuestionInput,
  TextValuePair,
  QuestionRadio,
  QuestionSelect,
  QuestionIncome,
  QuestionUploader,
  QuestionMultiButton,
  QuestionStateSelect,
  QuestionCheckboxMulti,
  QuestionApiCallLoader,
  QuestionInfoAccordion,
  QuestionRateSelection,
  QuestionMultiSelectButton,
} from '@mortgage-pos/types'
import SSNModal from './SSNModal'
import { AmazonBenefits } from './AmazonBenefits'
import { PlaidProvider } from './plaid/PlaidContext'
import AUSResults from './AUSResults'

type OnChangeType = (event: React.ChangeEvent) => {}
type OnBlurType = (event: React.ChangeEvent) => {}

interface SingleQuestionProps {
  question: any
  questionGroup?: string
  questionGroupValues?: object
  onChange?: (event?: React.ChangeEvent) => void
}

export const SingleQuestion: FC<SingleQuestionProps> = ({
  question,
  questionGroup,
  questionGroupValues,
  onChange: onQuestionChange,
}) => {
  const bankrateCompareEnabled = useVariation(
    FEATURE_FLAG_TYPES.BANKRATE_COMPARE_ENABLED
  )

  const { licensedStatesCodes } = useStoreState((state) => state.licensedStates)
  const questionName: string = question.questionName.name
  const { fieldInputted } = useStoreActions((state) => state.tracking)

  if (questionGroup && questionName.includes('autoCompleteAddressFields')) {
    return (
      <RepeatableAutoComplete
        questionGroup={questionGroup}
        name={questionName}
        title={question.title}
        modifierClass={question.modifierClass}
      />
    )
  }

  return (
    <Field name={questionName}>
      {({
        field: { onChange: mutatedChange },
        meta: { value, error, touched },
        form: { setFieldValue, setFieldTouched, submitForm },
      }) => {
        if (question.component === QuestionType.Hidden) {
          return null
        }

        const onBlur: OnBlurType = (event) => {
          fieldInputted({
            questionName,
            value,
            subTitle: question.subTitle,
          })

          return {}
        }

        const onChange: OnChangeType = (event) => {
          mutatedChange(event)
          onQuestionChange && onQuestionChange(event)
          return {}
        }

        if (question.component === QuestionType.BinaryButton) {
          return (
            <BinaryButton
              name={questionName}
              title={question.title}
              subtitle={question.subTitle}
              infoContent={question.infoContent}
              value={value}
              modifierClass={question.modifierClass}
              onChange={(value) => {
                setFieldValue(questionName, value)
                fieldInputted({
                  questionName,
                  value,
                  subTitle: question.subTitle,
                })
                onQuestionChange && onQuestionChange()
              }}
            />
          )
        }
        if (question.component === QuestionType.Income) {
          question = question as QuestionIncome
          return (
            <Income
              name={questionName}
              title={question.title}
              type={question.inputType}
              options={question.options}
              subTitle={question.subTitle}
              placeholder={question.placeholder}
              infoTitle={question.infoTitle}
              infoContent={question.infoContent}
              infoWarning={question.infoWarning}
              modifierClass={question.modifierClass}
              value={value}
              setValue={(value) => {
                setFieldValue(questionName, value)
                setFieldTouched(questionName, true, false)
                fieldInputted({
                  questionName,
                  value,
                  subTitle: question.subTitle,
                })
              }}
              onChange={onChange}
              onBlur={onBlur}
              touched={touched}
              error={error}
            />
          )
        }
        if (question.component === QuestionType.MultiButton) {
          question = question as QuestionMultiButton

          return (
            <MultiButton
              name={questionName}
              title={question.title}
              value={value}
              options={question.options}
              onChange={onChange}
              onBlur={onBlur}
              setValue={(value) => {
                setFieldValue(questionName, value)
                setFieldTouched(questionName, true, false)
                fieldInputted({
                  questionName,
                  value,
                  subTitle: question.subTitle,
                })
              }}
              touched={touched}
              error={error}
              modifierClass={question.modifierClass}
            />
          )
        }
        if (question.component === QuestionType.Checkbox) {
          return (
            <Checkbox
              content={question.content}
              error={error}
              modifierClass={question.modifierClass}
              name={questionName}
              setValue={(value) => {
                setFieldValue(questionName, value)
                setFieldTouched(questionName, true, false)
                fieldInputted({
                  questionName,
                  value,
                  subTitle: question.subTitle,
                })
              }}
              title={question.title}
              touched={touched}
              value={value}
              infoContent={question.infoContent}
            />
          )
        }
        if (question.component === QuestionType.Radio) {
          question = question as QuestionRadio
          return (
            <Radio
              title={question.title}
              subTitle={question.subTitle}
              options={question.options}
              name={questionName}
              modifierClass={question.modifierClass}
              value={value}
              infoContent={question.infoContent}
              setFieldValue={(value) => {
                setFieldValue(questionName, value)
                fieldInputted({
                  questionName,
                  value,
                  subTitle: question.subTitle,
                })
              }}
              error={error}
              touched={touched}
            />
          )
        }
        if (question.component === QuestionType.Input) {
          question = question as QuestionInput
          return (
            <Input
              name={questionName}
              title={question.title}
              type={question.inputType}
              maxLength={question.maxLength}
              subTitle={question.subTitle}
              placeholder={question.placeholder}
              infoTitle={question.infoTitle}
              infoContent={question.infoContent}
              infoWarning={question.infoWarning}
              modifierClass={question.modifierClass}
              value={value}
              setValue={(value) => {
                setFieldValue(questionName, value)
                setFieldTouched(questionName, true, false)
                fieldInputted({
                  questionName,
                  value,
                  subTitle: question.subTitle,
                })
              }}
              onChange={onChange}
              onBlur={onBlur}
              touched={touched}
              error={error}
            />
          )
        }
        if (question.component === QuestionType.Select) {
          question = question as QuestionSelect

          return (
            <SelectDropdown
              name={questionName}
              title={question.title}
              subTitle={question.subTitle}
              modifierClass={question.modifierClass}
              infoContent={question.infoContent}
              options={question.options}
              value={value}
              setValue={(value) => {
                setFieldValue(questionName, value)
                setFieldTouched(questionName, true, false)
                fieldInputted({
                  questionName,
                  value,
                  subTitle: question.subTitle,
                })
              }}
              touched={touched}
              error={error}
            />
          )
        }
        if (question.component === QuestionType.MultiSelectButton) {
          question = question as QuestionMultiSelectButton

          return (
            <MultiSelectButton
              name={questionName}
              title={question.title}
              subTitle={question.subTitle}
              modifierClass={question.modifierClass}
              infoContent={question.infoContent}
              options={question.options}
              value={value}
              setValue={(value) => {
                setFieldValue(questionName, value)
                setFieldTouched(questionName, true, false)
                fieldInputted({
                  questionName,
                  value,
                  subTitle: question.subTitle,
                })
              }}
              touched={touched}
              error={error}
            />
          )
        }
        if (question.component === QuestionType.StateSelect) {
          question = question as QuestionStateSelect

          const options: TextValuePair[] = licensedStatesCodes
            ? mapStatesDropdownData(licensedStatesCodes)
            : stateTextValuePairs

          return (
            <SelectDropdown
              name={questionName}
              title={question.title}
              subTitle={question.subTitle}
              modifierClass={question.modifierClass}
              infoContent={question.infoContent}
              options={options}
              value={value}
              setValue={(value) => {
                setFieldValue(questionName, value)
                setFieldTouched(questionName, true, false)
                fieldInputted({
                  questionName,
                  value,
                  subTitle: question.subTitle,
                })
              }}
              touched={touched}
              error={error}
            />
          )
        }

        if (question.component === QuestionType.CheckboxMulti) {
          question = question as QuestionCheckboxMulti
          return (
            <CheckboxMulti
              name={questionName}
              content={question.content}
              bottomContent={question.bottomContent}
              title={question.title}
              options={question.options}
              value={value}
              setField={(fields) => {
                for (const [questionName, value] of Object.entries(fields)) {
                  setFieldValue(questionName, value)
                  setFieldTouched(questionName, true, false)
                  fieldInputted({
                    questionName,
                    value,
                    subTitle: question.subTitle,
                  })
                }
              }}
              modifierClass={question.modifierClass}
              infoContent={question.infoContent}
              multiAnswerEnabled={question.multiAnswerEnabled}
              error={error}
              touched={touched}
              questionGroup={questionGroup}
              questionGroupValues={questionGroupValues}
            />
          )
        }

        if (question.component === QuestionType.NTLRateSelection) {
          return <NtlRateSelection />
        }

        if (question.component === QuestionType.CompareLERateSelection) {
          if (bankrateCompareEnabled) {
            return <BankrateCompareLERateSelection />
          } else {
            return (
              <CompareLERateSelection
                redirectToSlug={question.redirectToSlug}
              />
            )
          }
        }

        if (question.component === QuestionType.RateSelection) {
          return <RateSelection />
        }

        if (question.component === QuestionType.APICallLoader) {
          question = question as QuestionApiCallLoader

          return (
            <LoaderWithAPICalls
              title={question.title}
              subTitle={question.subTitle}
              apiCall={question.apiCall}
              minimumTimeout={Number(question.minimumTimeout)}
              errorMessage={question.errorMessage}
              continueOnError={question.continueOnError}
            />
          )
        }

        if (question.component === QuestionType.Uploader) {
          question = question as QuestionUploader

          if (bankrateCompareEnabled) {
            return (
              <BankrateUploader
                topSubHeader={question.topSubHeader}
                title={question.title}
                subTitle={question.subTitle}
                uploadButtonText={question.uploadButtonText}
                documentType={question.documentType}
              />
            )
          } else {
            return (
              <Uploader
                topSubHeader={question.topSubHeader}
                title={question.title}
                subTitle={question.subTitle}
                uploadButtonText={question.uploadButtonText}
                documentType={question.documentType}
              />
            )
          }
        }

        if (question.component === QuestionType.InfoAccordion) {
          question = question as QuestionInfoAccordion

          return (
            <InfoAccordion
              title={question.title}
              accordionContent={question.accordionContent}
            />
          )
        }

        if (question.component === QuestionType.RateSelection) {
          question = question as QuestionRateSelection

          return <RateSelection modifierClass={question.modifierClass} />
        }

        if (question.component === QuestionType.SSN) {
          return (
            <SSNModal onClose={() => {}} onSubmit={() => {}} isNTL={false} />
          )
        }

        if (question.component === QuestionType.PlaidAssets) {
          return (
            <PlaidProvider>
              <PlaidAssets />
            </PlaidProvider>
          )
        }

        if (question.component === QuestionType.AUSResults) {
          return <AUSResults />
        }

        if (question.component === QuestionType.AmazonBenefits) {
          return <AmazonBenefits />
        }
      }}
    </Field>
  )
}

export default SingleQuestion
